import React, { useEffect, useState } from "react";
import { Plus, KeyRound, Trash2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

export default function Vault() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand: "", platform: "", account: "", vault_ref: "", owner: "", two_fa: "Enabled", last_audit: "" });

  const load = () => api.get("/vault").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.brand || !form.platform) return toast.error("Brand and platform are required");
    try {
      await api.post("/vault", { ...form, last_audit: form.last_audit || new Date().toISOString() });
      toast.success("Vault reference added");
      setOpen(false);
      setForm({ brand: "", platform: "", account: "", vault_ref: "", owner: "", two_fa: "Enabled", last_audit: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/vault/${id}`); load(); };

  return (
    <div>
      <PageHeader
        title="Access Vault Register"
        subtitle="Credential vault references — never raw passwords"
        actions={
          <button data-testid="add-vault-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Reference
          </button>
        }
      />
      <div className="p-8 space-y-5">
        <div className="flex items-start gap-3 bg-[#FFD600]/[0.06] border border-[#FFD600]/20 rounded-sm px-4 py-3">
          <ShieldAlert size={18} className="text-[#FFD600] mt-0.5 shrink-0" />
          <p className="text-sm text-zinc-300">
            Do not store raw passwords here. Store password-manager vault references, owner, recovery contact, 2FA state and access audit history only.
          </p>
        </div>

        {entries.length === 0 ? (
          <EmptyState icon={KeyRound} title="No vault references" hint="Register your first credential reference." />
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/10 bg-[#0a0a0a]">
                  {["Brand", "Platform", "Account", "Vault Ref", "Owner", "2FA", "Last Audit", ""].map((h) => (
                    <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((v) => (
                  <tr key={v.id} data-testid={`vault-row-${v.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                    <td className="py-3 px-3 text-zinc-200">{v.brand}</td>
                    <td className="py-3 px-3 text-zinc-400">{v.platform}</td>
                    <td className="py-3 px-3 text-zinc-400">{v.account}</td>
                    <td className="py-3 px-3 font-mono text-xs text-[#4DA3FF]">{v.vault_ref}</td>
                    <td className="py-3 px-3 text-zinc-400">{v.owner}</td>
                    <td className="py-3 px-3">
                      <Chip className={v.two_fa === "Enabled" ? "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]" : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]"}>{v.two_fa}</Chip>
                    </td>
                    <td className="py-3 px-3 font-mono text-xs text-zinc-500">{(v.last_audit || "").slice(0, 10)}</td>
                    <td className="py-3 px-3">
                      <button data-testid={`delete-vault-${v.id}`} onClick={() => remove(v.id)} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Vault Reference</DialogTitle><DialogDescription className="sr-only">Register a credential vault reference — never store raw passwords.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="vault-brand-input" className={inputCls} placeholder="Brand *" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} />
              <input data-testid="vault-platform-input" className={inputCls} placeholder="Platform *" value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Account" value={form.account} onChange={(e) => setForm((f) => ({ ...f, account: e.target.value }))} />
              <input className={inputCls} placeholder="Vault reference (e.g. VAULT-XX)" value={form.vault_ref} onChange={(e) => setForm((f) => ({ ...f, vault_ref: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Owner" value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} />
              <select className={inputCls} value={form.two_fa} onChange={(e) => setForm((f) => ({ ...f, two_fa: e.target.value }))}>
                {["Enabled", "Disabled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button data-testid="vault-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">Add Reference</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
